<template>
  <div class="nwx-picklist"
       v-click-outside="hideDropdown"
  >
    <div :class="{
        'picklist-cont': true,
        'opened': isDropdownVisible,
        'focused': focused,
        'disabled': disabled,
        'error': error,
      }"
         @click.stop="openDropdown()"
         @keyup.enter="openDropdown()"
    >
      <input type="text"
             :name="name"
             class="picklist-input"
             ref="input"
             :placeholder="placeholder"
             v-model="localDisplayed"
             :readonly="true"
             :disabled="disabled"
             @blur="$emit('blur')"
      >
    </div>
    <div :class="{
        'picklist-dropdown': true,
        'visible': isDropdownVisible,
      }"
         ref="dropdown"
    >
      <div class="dropdown-wrapper">
        <div
          class="option-row"
          v-for="(option, index) in itemsValues"
          :key="index"
        >
          <label :class="{
                'checkbox-container-custom': true,
                'checked': localSelected && localSelected.indexOf(option.value) !== -1
              }"
                 :for="`checkbox-id-${uid}${index}`"
          >
            <slot name="option-text" :option="option">
              <div class="label-text" v-html="option.text"></div>
            </slot>
            <input type="checkbox"
                   :value="option.value"
                   :name="`checkbox-id-${uid}`"
                   :id="`checkbox-id-${uid}${index}`"
                   v-model="localSelected"
                   autocomplete="off"
            >
            <span class="checkmark"></span>

            <label class="num-of-computers-container"
                   v-if="localSelected && localSelected.indexOf(option.value) !== -1"
                   :for="`number-input-id-${uid}${index}`"
            >
              <slot name="option-text" :option="option">
                <div class="label-text">{{option.label}}<span class="required">*</span></div>
              </slot>
              <input class="input"
                     type="text"
                     v-model="localNumbers[option.value]"
                     :id="`number-input-id-${uid}${index}`"
              >
            </label>
          </label>
        </div>
      </div>
      <div class="footer">Total: {{totalNumberOfComputers}}</div>
    </div>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';

export default {
    name: 'NwxComputersToProtectSelect',
    directives: {
      ClickOutside,
    },
    props: {
      name: {
        type: String,
        default: '',
      },
      selected: {
        type: [String, Array],
        default: undefined,
      },
      items: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: '',
      },
      focused: Boolean,
      disabled: Boolean,
      limit: Number,
      error: Boolean,
    },
    model: {
      prop: 'selected',
      event: 'picklist:changed',
    },
    data() {
      return {
        // eslint-disable-next-line
        uid: this._uid,
        localSelected: [],
        localDisplayed: '',
        localNumbers: {},
        nativeChangeEnabled: true,
        programChangeEnabled: false,
        isDropdownVisible: false,
        isDropdownScrollable: false,
        itemsValues: [],
        allValues: [],
      };
    },
    computed: {
      totalNumberOfComputers() {
        let total = 0;

        Object.values(this.localNumbers).forEach((number) => {
          if (number > 0) {
            total += Number(number);
          }
        });

        return total;
      },
    },
    watch: {
      isDropdownVisible(isVisible) {
        if (isVisible) {
          const dropdownHeight = this.$refs.dropdown.clientHeight;
          this.isDropdownScrollable = (dropdownHeight >= 280);
        } else {
          this.isDropdownScrollable = false;
        }
      },
      selected(selected) {
        if (this.programChangeEnabled) {
          this.nativeChangeEnabled = false;
          const selectedOpt = this.getSelectedOpt(selected);
          this.localSelected = selectedOpt.value;
        }
        this.programChangeEnabled = true;
      },
      localSelected(selected) {
        const selectedOpt = this.getSelectedOpt(selected);

        if (this.totalNumberOfComputers > 0) {
          this.localDisplayed = `${selectedOpt.text}. (Total: ${this.totalNumberOfComputers})`;
        } else {
          this.localDisplayed = selectedOpt.text;
        }

        if (this.nativeChangeEnabled) {
          this.programChangeEnabled = false;
          this.$emit('picklist:changed', selectedOpt.value);
          this.$emit('change');
        }
        this.nativeChangeEnabled = true;

        const needToResetValues = this.allValues
          .filter((value) => !selectedOpt.value.includes(value));

        const tempObj = { ...this.localNumbers };

        needToResetValues.forEach((value) => {
          tempObj[value] = undefined;
        });

        this.localNumbers = tempObj;
      },
      totalNumberOfComputers(total) {
        if (total > 0) {
          this.localDisplayed = `${this.localSelected}. (Total: ${total})`;
        } else {
          this.localDisplayed = `${this.localSelected}`;
        }

        this.$emit('number-of-computers-updated', this.localNumbers);
      },
    },
    methods: {
      openDropdown() {
        // eslint-disable-next-line
        this.$root.$emit('picklist:close-all', this._uid);
        if (!this.disabled) {
          if (this.autocomplete) {
            this.isDropdownVisible = true;
          } else {
            this.isDropdownVisible = !this.isDropdownVisible;
          }

          if (this.autocomplete && !this.multiple) {
            this.$refs.input.select();
          }
        }
      },
      hideDropdown(uid) {
        // eslint-disable-next-line
        if (uid !== this._uid) {
          this.isDropdownVisible = false;
        }
      },
      getSelectedOpt(selected) {
        let itemValue;
        let itemText = '';

        itemValue = [];

        if (Array.isArray(selected)) {
          const filtered = [];

          selected.forEach((item) => {
            const newItem = this.itemsValues.find((
              (v) => v.value === item
            ));
            if (newItem) {
              filtered.push(newItem);
            }
          });

          itemValue = filtered.map((
            (item) => item.value
          ));

          itemText = filtered.map((
            (item) => item.text
          )).join('; ');
        }

        return { value: itemValue, text: itemText };
      },
    },
    updated() {
      this.itemsValues = this.items;
      this.allValues = this.itemsValues.map((item) => item.value);
      this.itemsValues.forEach((item) => {
        if (item.numOfComputers) {
          this.localNumbers[item.value] = item.numOfComputers;
        }
      });
    },
  };
</script>

<style lang="less" scoped>

@import '../assets/variables';

.nwx-picklist {
  position: relative;

  .picklist-cont {
    .picklist-input {
      display: block;
      width: 100%;
      height: 40px;
      padding: 9px 45px 9px 16px;
      font-size: 1rem;
      text-overflow: ellipsis;
      background-color: #fff;
      border: 1px solid @inputGray;
      transition: border-color ease-out .2s;
      -moz-appearance: none;
      -webkit-appearance: none;
      outline: 0;

      &::placeholder {
        color: fade(@mainGray, 80%);
      }

      &:hover {
        border-color: @labelGray;
      }

      &:focus {
        border-color: @vigilantBlue !important;
      }

      &::-ms-clear {
        display: none;
      }

      &[disabled],
      &[readonly] {
        opacity: 1;
      }

      &[readonly] {
        cursor: pointer;
      }

      &[disabled] {
        cursor: default;
        color: fade(@mainGray, 80%);
        border-color: fade(@mainGray, 20%) !important;
        background-color: fade(@bodyWhite, 60%);
      }
    }

    &:after {
      content: ' ';
      display: block;
      width: 10px;
      height: 6px;
      position: absolute;
      top: 18px;
      right: 19px;
      pointer-events: none;
      background-image: url('//img.netwrix.com/elements/partnerPortal/select_caret_down.svg');
      background-repeat: no-repeat;
      background-position: center;
    }

    &.opened {
      .picklist-input {
        border-color: @vigilantBlue !important;
        border-bottom-color: transparent !important;
      }

      &:after {
        top: 17px;
        transform: rotate(180deg);
      }
    }

    &.dense {
      .picklist-input {
        height: 30px;
        padding: 4px 45px 4px 16px;
      }

      &:after {
        top: 13px;
      }

      &.opened {
        &:after {
          top: 12px;
        }
      }
    }

    &.focused {
      .picklist-input {
        border-color: @vigilantBlue;
      }
    }

    &.disabled {
      &:after {
        opacity: 0.4;
      }
    }

    &.error {
      .picklist-input {
        border-color: @mainRed;
      }
    }
  }

  .picklist-dropdown {
    min-width: 100%;
    //max-height: 282px;
    background: #fff;
    border: 1px solid @inputGray;
    position: absolute;
    top: 39px;
    left: 0;
    pointer-events: none;
    overflow: hidden;
    visibility: hidden;
    opacity: 0;
    z-index: 2;
    transition: visibility 0.2s, opacity 0.2s;

    &.visible {
      border-color: @vigilantBlue;
      border-top-color: @inputGray;
      pointer-events: auto;
      overflow: initial;
      visibility: visible;
      opacity: 1;
    }

    &.scrollable {
      overflow: auto;
    }

    &.dense {
      top: 29px;
    }

    .dropdown-wrapper {
      //max-height: 218px;
      //overflow: auto;
    }

    .no-option-msg {
      height: 40px;
      padding: 10px 16px;
      cursor: default;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }

    .option-row {
      &.disabled-row {
        background: @bodyWhite;

        .checkbox-container-custom {
          color: #aaa;
          &.checked {
            color: #aaa;

            .checkmark {
              border-color: @vigilantBlue;
              background-color: @accessWhite;
            }
          }
        }
      }

      .checkbox-container-custom {
        display: block;
        position: relative;
        cursor: pointer;
        width: 100%;
        //height: 40px;
        margin: 0;
        font-weight: 400;
        font-size: 1rem;
        white-space: nowrap;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;

        &:hover {
          .checkmark {
            border: 1px solid @vigilantBlue;
            background: #5851db33;
          }
        }

        &.checked {
          .label-text {
            color: @vigilantBlue;
          }

          .num-of-computers-container {
            width: 100%;
            padding: 8px 16px 19px 16px;
            margin-bottom: 0;

            .label-text {
              margin-bottom: 8px;
              padding: 0;
              font-size: .75rem;
              font-style: normal;
              font-weight: 400;
              line-height: 1.4;

              .required {
                color: #DA262E;
                font-size: 1rem;
                font-style: normal;
                font-weight: 500;
                line-height: normal;
                letter-spacing: 2.2px;
                text-transform: uppercase;
              }
            }

            .input {
              position: static;
              opacity: 1;
              cursor: auto;
              display: block;
              width: 100%;
              height: 40px;
              padding: 9px 16px;
              font-size: 1rem;
              font-weight: 400;
              line-height: 1.42857143;
              color: @mainGray;
              background-image: none;
              border: 1px solid @inputGray;
              transition: border-color ease-out .2s;
              -moz-appearance: none;
              -webkit-appearance: none;
              outline: 0;

              &::placeholder {
                color: fade(@mainGray, 80%);
              }

              &:hover {
                border-color: @labelGray;
              }

              &:focus {
                border-color: @vigilantBlue !important;
              }

              &::-ms-clear, &::-ms-reveal {
                display: none;
              }

              &[disabled],
              &[readonly] {
                opacity: 1;
              }

              &[disabled] {
                cursor: default;
                color: fade(@mainGray, 80%);
                border-color: fade(@mainGray, 20%) !important;
                background-color: fade(@bodyWhite, 60%);
              }

              &.combobox {
                cursor: pointer;
                padding-right: 45px;
                background-image:
                  url('//img.netwrix.com/elements/partnerPortal/select_caret_down.svg');
                background-repeat: no-repeat;
                background-position: top 17px right 18px;
              }

              &.textbox {
                height: auto;
                min-height: 80px;
                max-height: 640px;
                resize: vertical;
              }

              &.focused {
                border-color: @vigilantBlue;
              }

              &.error {
                border-color: @mainRed;
              }
            }
          }

          .checkmark {
            border-color: @vigilantBlue;
            background-color: @vigilantBlue;

            &:after {
              opacity: 1;
            }
          }
        }

        input {
          position: absolute;
          cursor: pointer;
          opacity: 0;
          height: 0;
          width: 0;
        }
      }

      .checkbox-container-custom {
        .label-text {
          padding: 10px 21px 10px 42px;
        }

        input {
          &:checked ~ .checkmark {
            border-color: @vigilantBlue;
            background-color: @vigilantBlue;
          }

          &:checked ~ .checkmark:after {
            opacity: 1;
          }
        }

        .checkmark {
          position: absolute;
          top: 12px;
          left: 16px;
          height: 16px;
          width: 16px;
          border: 1px solid @inputGray;
          transition: background 0.2s, border 0.2s;

          &:after {
            content: " ";
            display: block;
            width: 100%;
            height: 100%;
            opacity: 0;
          }
        }
      }

      .children {
        .checkbox-container-custom {
          padding-left: 40px;

          .checkmark {
            left: 42px;
          }
        }
      }
    }

    .footer {
      position: relative;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: auto;
      padding: 14px 16px;
      color: @colorH;
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
      line-height: 1.4;
      border-top: 1px solid #D1D4DD;
    }
  }
}
</style>
